// src/pages/SlideshowPlayer.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { studentSlideshowAPI, studentTutorialAPI } from '../services/api';

const SlideshowPlayer = () => {
    const { studentTutorialId, chunkIndex } = useParams();
    const [slideshow, setSlideshow] = useState(null);
    const [parentHomeworkId, setParentHomeworkId] = useState(null);
    const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const audioRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

    // --- FIXED: Wrapped in useCallback ---
    const getFullUrl = useCallback((path) => {
        if (!path) return '';
        return path.startsWith('http') ? path : `${API_BASE_URL}/${path}`;
    }, [API_BASE_URL]);

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const [slideshowRes, tutorialRes] = await Promise.all([
                studentSlideshowAPI.getSlideshowChunk(studentTutorialId, chunkIndex),
                studentTutorialAPI.getById(studentTutorialId).catch(() => ({ data: null }))
            ]);
            if (tutorialRes.data?.homeworkId) setParentHomeworkId(tutorialRes.data.homeworkId);
            if (slideshowRes.data.status === 'COMPLETED') {
                setSlideshow(slideshowRes.data);
            } else {
                setError('Slideshow not ready.');
            }
        } catch (err) { setError('Failed to load slideshow.'); } 
        finally { setLoading(false); }
    }, [studentTutorialId, chunkIndex]);

    useEffect(() => { fetchData(); }, [fetchData]);

    useEffect(() => {
        const el = audioRef.current;
        const stop = () => setIsPlaying(false);
        const start = () => setIsPlaying(true);
        if (el) {
            el.addEventListener('ended', stop);
            el.addEventListener('pause', stop);
            el.addEventListener('play', start);
            return () => {
                el.removeEventListener('ended', stop);
                el.removeEventListener('pause', stop);
                el.removeEventListener('play', start);
            };
        }
    }, []);

    const currentSlide = slideshow?.slides?.[currentSlideIndex];

    useEffect(() => {
        if (currentSlide?.audioUrl && audioRef.current) {
            audioRef.current.src = getFullUrl(currentSlide.audioUrl);
            audioRef.current.load();
            setIsPlaying(false);
        }
    }, [currentSlide, API_BASE_URL, getFullUrl]); // Added getFullUrl

    if (loading) return <p>Loading...</p>;
    if (error) return <p style={{color:'red'}}>{error}</p>;
    if (!slideshow) return <p>No slides.</p>;

    const handleNext = () => {
        if (currentSlideIndex < slideshow.slides.length - 1) {
            if(audioRef.current) audioRef.current.pause();
            setCurrentSlideIndex(p => p + 1);
        }
    };
    const handlePrev = () => {
        if (currentSlideIndex > 0) {
            if(audioRef.current) audioRef.current.pause();
            setCurrentSlideIndex(p => p - 1);
        }
    };
    const togglePlay = () => {
        if(!audioRef.current) return;
        isPlaying ? audioRef.current.pause() : audioRef.current.play().catch(console.error);
    };

    return (
        <div style={{ padding: '20px', maxWidth: '800px', margin: '20px auto', textAlign: 'center', border: '1px solid #ccc', borderRadius: '8px' }}>
            {parentHomeworkId && <Link to={`/homework/${parentHomeworkId}`} style={{display:'block', marginBottom:'15px', textAlign:'left'}}>← Back</Link>}
            <h2>Student Slideshow - Chunk {parseInt(chunkIndex) + 1}</h2>
            <div style={{ margin: '20px 0' }}>
                <img 
                    src={getFullUrl(currentSlide.imageUrl)} 
                    alt="Slide" 
                    style={{maxWidth:'100%', maxHeight:'400px'}} 
                    onError={(e) => { e.target.onerror = null; e.target.src="https://placehold.co/600x400/CCCCCC/FFFFFF?text=Image+Load+Error"; }}
                />
            </div>
            <p style={{padding:'10px', background:'#f9f9f9'}}>{currentSlide.narration}</p>
            <audio ref={audioRef} style={{display:'none'}} />
            <div style={{marginTop:'20px', display:'flex', justifyContent:'center', gap:'10px'}}>
                <button onClick={handlePrev} disabled={currentSlideIndex===0}>Prev</button>
                <button onClick={togglePlay} disabled={!currentSlide.audioUrl}>{isPlaying ? 'Pause' : 'Play'}</button>
                <button onClick={handleNext} disabled={currentSlideIndex===slideshow.slides.length-1}>Next</button>
            </div>
        </div>
    );
};

export default SlideshowPlayer;